import { Row, Table as TanstackTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { memo } from 'react';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronUpIconV2 } from 'src/svgs/Viagogo/goicons/ChevronUpIconV2';

import * as tableStyles from '../components/Table.css';
import * as styles from '../GroupedTable.css';
import { TableRowCheckbox } from './TableCheckbox';

interface GroupHeaderRowProps<T> {
  groupId: string;
  childData: T[];
  isExpanded: boolean;
  toggleExpanded: () => void;
  renderGroupHeader?: (groupId: string, groupData: T[]) => React.ReactNode;
  showCheckbox?: boolean;
  parentRow: Row<T>;
  table: TanstackTable<T>;
  getGroupId?: (data: T) => string | null | undefined;
  getItemId?: (data: T) => string | undefined;
  isSelectionDisabled?: (data: T) => string | undefined;
  tableContainerWidth: string;
  selected?: boolean;
  active?: boolean;
  expandOnRowClick?: boolean;
}

const GroupHeaderRowRaw = <T,>({
  groupId,
  childData,
  isExpanded,
  toggleExpanded,
  renderGroupHeader,
  showCheckbox,
  parentRow,
  table,
  getGroupId,
  getItemId,
  isSelectionDisabled,
  tableContainerWidth,
  selected,
  expandOnRowClick,
}: GroupHeaderRowProps<T>) => {
  const Icon = isExpanded ? ChevronUpIconV2 : ChevronDownIconV2;

  return (
    <td className={clsx(styles.groupHeaderCellTd)} colSpan={1}>
      <div
        className={clsx(
          styles.groupHeaderCell,
          showCheckbox && styles.groupHeaderCellWithCheckbox,
          selected && tableStyles.tableRowSelected
        )}
        style={{
          width: `var(${tableContainerWidth})`,
          cursor: expandOnRowClick ? 'pointer' : undefined,
        }}
        onClick={
          expandOnRowClick
            ? () => {
                if (!isExpanded) {
                  toggleExpanded();
                }
              }
            : undefined
        }
      >
        <Stack
          gap="m"
          alignItems="center"
          direction="row"
          style={{ flex: 1, overflow: 'auto', minWidth: 0 }}
        >
          {showCheckbox && (
            <div
              style={{ paddingRight: vars.spacing.xxs }}
              onClick={
                expandOnRowClick ? (e) => e.stopPropagation() : undefined
              }
            >
              <TableRowCheckbox
                row={parentRow}
                table={table}
                getGroupId={getGroupId}
                getItemId={getItemId}
                isSelectionDisabled={isSelectionDisabled}
              />
            </div>
          )}
          {renderGroupHeader &&
            groupId &&
            renderGroupHeader(groupId, childData)}
        </Stack>
        <Icon
          size={vars.iconSize.s}
          withHoverEffect
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleExpanded();
          }}
        />
      </div>
    </td>
  );
};

export const GroupHeaderRow = memo(
  GroupHeaderRowRaw
) as typeof GroupHeaderRowRaw;
